import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "../lib/analytics";

const copy = {
  pt: {
    heading: "Não procure mais...\nnão está aqui nada!",
    code: "ERRO: 404",
    back: "Voltar",
    home: "Página Inicial",
  },
  en: {
    heading: "Don't roam around looking\nfor what you know is not here...",
    code: "Error: 404",
    back: "Go Back",
    home: "Homepage",
  },
};

const styles = `
  :root {
    --brand-primary:   #4CB1A1;
    --brand-secondary: #1E6E65;
  }

  @media (prefers-color-scheme: dark) {
    :root { --brand-secondary: #155f58; }
  }

  /* ── Float ── */
  .cloud-float {
    animation: cloudFloat 4.2s ease-in-out infinite;
  }
  @keyframes cloudFloat {
    0%, 100% { transform: translateY(0px);   }
    50%       { transform: translateY(-18px); }
  }

  /* ── Blink: open eyes ── */
  .cloud-eye {
    animation: eyeOpen 5.5s ease-in-out infinite;
  }
  @keyframes eyeOpen {
    0%, 85%, 100% { opacity: 1; }
    89%, 96%      { opacity: 0; }
  }

  /* ── Blink: closed eyes (opposite phase) ── */
  .cloud-eyes-closed {
    animation: eyeClose 5.5s ease-in-out infinite;
  }
  @keyframes eyeClose {
    0%, 85%, 100% { opacity: 0; }
    89%, 96%      { opacity: 1; }
  }

  /* ── Brows dip on blink ── */
  .cloud-brow--left,
  .cloud-brow--right {
    animation: browDip 5.5s ease-in-out infinite;
    transform-box:    fill-box;
    transform-origin: center;
  }
  @keyframes browDip {
    0%, 85%, 100% { transform: translateY(0px); }
    89%, 96%      { transform: translateY(4px); }
  }

  /* ── Reduced motion ── */
  @media (prefers-reduced-motion: reduce) {
    .cloud-float,
    .cloud-eye,
    .cloud-eyes-closed,
    .cloud-brow--left,
    .cloud-brow--right { animation: none; }
    .cloud-eyes-closed  { opacity: 0; }
    .cloud-eye          { opacity: 1; }
  }
`;

/*
  SVG layout (viewBox 0 0 400 620):
  ┌─────────────────────────┐  y=0
  │       string            │
  │         │               │
  │         ● knot  y=255   │
  ├─────────────────────────┤  y=260
  │                         │
  │      cloud body         │  (original coords + translate 205)
  │      cloud face         │  cloud top ≈ y=260, bottom ≈ y=455
  │                         │
  └─────────────────────────┘  y=620
*/
function CloudIllustration({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 400 620"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ overflow: "visible" }}
    >
      {/* ── String (static — outside float group) ── */}
      <line
        x1="200" y1="0"
        x2="200" y2="252"
        stroke="#CBD5E1"
        strokeWidth="1.5"
        strokeLinecap="round"
      />

      {/* ── Floating assembly ── */}
      <g className="cloud-float">
        {/* Knot circle at string end */}
        <circle cx="200" cy="255" r="8" fill="white" stroke="#CBD5E1" strokeWidth="1.5" />

        {/* Cloud body + face, shifted down 205px so top aligns with knot */}
        <g transform="translate(0,205)">
          <g className="cloud-body">
            <path d="M100 215C138.66 215 170 185.899 170 150C170 114.101 138.66 85 100 85C61.3401 85 30 114.101 30 150C30 185.899 61.3401 215 100 215Z" fill="var(--brand-primary)"/>
            <path d="M170 205C214.183 205 250 171.421 250 130C250 88.5786 214.183 55 170 55C125.817 55 90 88.5786 90 130C90 171.421 125.817 205 170 205Z" fill="var(--brand-primary)"/>
            <path d="M250 205C291.421 205 325 173.66 325 135C325 96.3401 291.421 65 250 65C208.579 65 175 96.3401 175 135C175 173.66 208.579 205 250 205Z" fill="var(--brand-primary)"/>
            <path d="M310 215C345.899 215 375 188.137 375 155C375 121.863 345.899 95 310 95C274.101 95 245 121.863 245 155C245 188.137 274.101 215 310 215Z" fill="var(--brand-primary)"/>
            <path d="M200 250C266.274 250 320 214.183 320 170C320 125.817 266.274 90 200 90C133.726 90 80 125.817 80 170C80 214.183 133.726 250 200 250Z" fill="var(--brand-primary)"/>
          </g>
          <g className="cloud-face">
            <g className="cloud-eye cloud-eye--left">
              <path d="M150 158C163.807 158 175 149.941 175 140C175 130.059 163.807 122 150 122C136.193 122 125 130.059 125 140C125 149.941 136.193 158 150 158Z" fill="white"/>
              <path d="M155 152C161.627 152 167 146.627 167 140C167 133.373 161.627 128 155 128C148.373 128 143 133.373 143 140C143 146.627 148.373 152 155 152Z" fill="var(--brand-secondary)"/>
              <path opacity="0.7" d="M158 142C160.761 142 163 139.761 163 137C163 134.239 160.761 132 158 132C155.239 132 153 134.239 153 137C153 139.761 155.239 142 158 142Z" fill="white"/>
            </g>
            <g className="cloud-eye cloud-eye--right">
              <path d="M250 158C263.807 158 275 149.941 275 140C275 130.059 263.807 122 250 122C236.193 122 225 130.059 225 140C225 149.941 236.193 158 250 158Z" fill="white"/>
              <path d="M245 152C251.627 152 257 146.627 257 140C257 133.373 251.627 128 245 128C238.373 128 233 133.373 233 140C233 146.627 238.373 152 245 152Z" fill="var(--brand-secondary)"/>
              <path opacity="0.7" d="M242 142C244.761 142 247 139.761 247 137C247 134.239 244.761 132 242 132C239.239 132 237 134.239 237 137C237 139.761 239.239 142 242 142Z" fill="white"/>
            </g>
            <g className="cloud-eyes-closed">
              <path d="M130 140C143.333 143.333 156.667 143.333 170 140" stroke="var(--brand-secondary)" strokeWidth="5" strokeLinecap="round"/>
              <path d="M230 140C243.333 143.333 256.667 143.333 270 140" stroke="var(--brand-secondary)" strokeWidth="5" strokeLinecap="round"/>
            </g>
            <path className="cloud-brow cloud-brow--left"  d="M130 125L170 115" stroke="var(--brand-secondary)" strokeWidth="6" strokeLinecap="round"/>
            <path className="cloud-brow cloud-brow--right" d="M270 115L230 125" stroke="var(--brand-secondary)" strokeWidth="6" strokeLinecap="round"/>
            <path className="cloud-mouth" d="M170 180C183.333 186.667 196.667 188.333 210 185" stroke="var(--brand-secondary)" strokeWidth="5" strokeLinecap="round"/>
          </g>
        </g>
      </g>
    </svg>
  );
}

export default function NotFound({ lang = "pt", onLanguageChange }) {
  const navigate = useNavigate();
  const t = copy[lang] || copy.pt;

  /* Remove the navbar padding-top that is set globally */
  useEffect(() => {
    const prev = document.body.style.paddingTop;
    document.body.style.paddingTop = "0";
    return () => { document.body.style.paddingTop = prev; };
  }, []);

  useEffect(() => {
    trackEvent("page_not_found", {
      attempted_path: `${window.location.pathname}${window.location.search}${window.location.hash}`,
      language: lang,
    });
  }, [lang]);

  return (
    <>
      <style>{styles}</style>

      <div className="min-h-screen flex flex-col sm:flex-row">

        {/* ══════════════════════════════════════════
            LEFT column — text content
            on mobile: order 2 (below cloud)
        ══════════════════════════════════════════ */}
        <div className="order-2 sm:order-1 sm:basis-[44%] lg:basis-[42%] flex flex-col justify-center
                        px-8 sm:pl-12 sm:pr-6 lg:pl-20 lg:pr-8
                        pt-10 pb-16 sm:py-0">

          {/* Logo */}
          <a
            href={lang === "en" ? "/en" : "/pt"}
            aria-label="Invisible Cloud — Início"
            className="inline-block leading-none mb-12 sm:mb-16"
          >
            <picture>
              <source media="(prefers-color-scheme: dark)" srcSet="/assets/logoWhite.png" />
              <img src="/assets/logo.png" alt="Invisible Cloud" className="h-[26px] w-auto" />
            </picture>
          </a>

          {/* Error badge */}
          <p className="text-[11px] font-bold tracking-[0.22em] text-[#4CB1A1] uppercase mb-3">
            {t.code}
          </p>

          {/* Heading */}
          <h1 className="text-[2rem] sm:text-[2.4rem] lg:text-[2.8rem]
                         font-bold leading-[1.2] whitespace-pre-line
                         text-[#1E2A5A] dark-heading mb-8">
            {t.heading}
          </h1>

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg
                         bg-[#4CB1A1] text-white font-semibold text-[15px]
                         hover:bg-[#2B8D80] transition-colors
                         focus-visible:outline-none focus-visible:ring-2
                         focus-visible:ring-[#4CB1A1] focus-visible:ring-offset-2"
            >
              <svg className="w-4 h-4 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M17 10a.75.75 0 0 1-.75.75H5.612l4.158 3.96a.75.75 0 1 1-1.04 1.08l-5.5-5.25a.75.75 0 0 1 0-1.08l5.5-5.25a.75.75 0 1 1 1.04 1.08L5.612 9.25H16.25A.75.75 0 0 1 17 10Z" clipRule="evenodd"/>
              </svg>
              {t.back}
            </button>
            <a
              href={lang === "en" ? "/en" : "/pt"}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg
                         border border-[#4CB1A1] text-[#4CB1A1] font-semibold text-[15px]
                         hover:bg-[#4CB1A1] hover:text-white transition-colors
                         focus-visible:outline-none focus-visible:ring-2
                         focus-visible:ring-[#4CB1A1] focus-visible:ring-offset-2"
            >
              {t.home}
            </a>
          </div>
        </div>

        {/* ══════════════════════════════════════════
            RIGHT column — cloud hangs from top
            on mobile: order 1 (above text), fixed height
            on desktop: fills full viewport height
        ══════════════════════════════════════════ */}
        <div className="order-1 sm:order-2 sm:basis-[56%] lg:basis-[58%] flex justify-center items-start overflow-hidden">
          <CloudIllustration className="h-[300px] sm:h-screen w-auto" />
        </div>

      </div>
    </>
  );
}
